import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Calendar, ArrowLeft, Award, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { rtdb } from "../../config/firebase";
import { ref, onValue, off } from "firebase/database";

export default function CertificatePage() {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Pastikan halaman mulai dari atas saat pertama kali dibuka
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 🔹 Ambil data dari Firebase (node GaleriCertificate)
  useEffect(() => {
    const galeriRef = ref(rtdb, "GaleriCertificate");

    const unsubscribe = onValue(
      galeriRef,
      (snapshot) => {
        const data = snapshot.val();
        const loaded = [];

        if (data) {
          // Ambil data dari Firebase dalam urutan natural (data terbaru sudah di atas)
          for (const id in data) {
            loaded.unshift({ id, ...data[id] }); // 🔁 gunakan unshift agar data terbaru tampil duluan
          }
        }

        setCertificates(loaded);
        setIsLoading(false);
      },
      (error) => {
        console.error("❌ Gagal memuat data:", error);
        setIsLoading(false);
      }
    );

    return () => off(galeriRef, "value", unsubscribe);
  }, []);

  // 🔒 Nonaktifkan scroll ketika modal terbuka
  useEffect(() => {
    if (selectedImage) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedImage]);

  // 🔍 Filter pencarian & tanggal
  const filtered = certificates.filter(
    (c) =>
      (c.judul?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.keterangan?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (!selectedDate || c.tanggal === selectedDate)
  );

  // 🔙 Tombol kembali otomatis
  const handleBack = () => {
    if (window.history.length > 2) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 🔙 Tombol Kembali */}
      <motion.button
        onClick={handleBack}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="fixed top-6 left-6 z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 transition-colors"
      >
        <ArrowLeft size={16} />
        <span className="text-sm font-semibold">Kembali</span>
      </motion.button>

      {/* 🟩 Hero Section */}
      <div className="relative bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 text-white py-24 md:py-32 px-6 text-center overflow-hidden">
        {/* Efek cahaya lembut */}
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-green-400/30 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-teal-400/20 rounded-full blur-[120px]" />
        </div>

        {/* Pola grid putih transparan */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.07)_1px,transparent_1px)] bg-[size:48px_48px]" />

        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/30 rounded-full backdrop-blur-sm mb-6">
            <Award className="w-4 h-4 text-white" />
            <span className="text-sm font-mono uppercase tracking-wider text-white/90">
              Sertifikat & Penghargaan
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            Pencapaian & Pengakuan Kami
          </h1>
          <p className="text-lg text-green-100 max-w-2xl mx-auto">
            Daftar sertifikat dan penghargaan resmi yang diperoleh oleh perusahaan kami.
          </p>
        </div>
      </div>

      {/* 🏆 Konten utama */}
      <div className="relative bg-white -mt-16 rounded-t-3xl shadow-xl py-16 px-6 md:px-16">
        <div className="max-w-7xl mx-auto">
          {/* 🔍 Search & Filter */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-12">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-4 top-3.5 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Cari sertifikat atau penghargaan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition"
              />
            </div>

            <div className="relative w-full max-w-xs">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-4 py-3 rounded-full border border-gray-300 bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition"
              />
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate("")}
                  className="absolute right-4 top-3 text-gray-400 hover:text-red-500 text-sm"
                  title="Hapus filter tanggal"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 📜 Grid Sertifikat */}
          {isLoading ? (
            <div className="flex justify-center py-16 text-gray-500">
              Memuat data sertifikat...
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((cert) => (
                <motion.div
                  key={cert.id}
                  whileHover={{ scale: 1.02 }}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col border border-gray-200"
                >
                  {/* 🖼️ Gambar */}
                  <div
                    className="relative h-56 cursor-pointer"
                    onClick={() => setSelectedImage(cert.fotoUrl)}
                  >
                    <img
                      src={cert.fotoUrl}
                      alt={cert.judul}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  </div>

                  {/* 📋 Info */}
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-bold text-xl text-gray-900 mb-1 group-hover:text-green-700 transition-colors">
                      {cert.judul}
                    </h3>
                    <p className="text-gray-600 text-sm flex-1 line-clamp-3">
                      {cert.keterangan}
                    </p>
                    <div className="flex items-center justify-between mt-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} /> {cert.tanggal}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-16">
              Tidak ada sertifikat yang cocok dengan pencarian atau tanggal.
            </div>
          )}
        </div>
      </div>

      {/* 🪟 Modal Foto */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 bg-white/80 rounded-full p-2 shadow hover:bg-white transition"
              >
                <X className="w-5 h-5 text-gray-800" />
              </button>
              <img
                src={selectedImage}
                alt="Preview Sertifikat"
                className="w-full h-auto object-contain max-h-[80vh]"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
